/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Divider,
  Stack,
  Paper,
} from "@mui/material";
import moment from "moment";
import { useParams } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CakeIcon from "@mui/icons-material/Cake";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import PinDropIcon from "@mui/icons-material/PinDrop";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import BadgeIcon from "@mui/icons-material/Badge";
import SchoolIcon from "@mui/icons-material/School";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";

// Assets & Utils
import gohonzonBackground from "../../../../assets/images/gohonzonBackground.png";
import { getInitials } from "../../../member/memberDetails/utlis";
import Button from "../../../../components/common/Button";
import { getSubDivisionDisplayName } from "../../../../utils/helper";

// --- Constants ---
const READ_MODE_ROUTES = [
  "verification",
  "recommended",
  "interview ",
  "tracking",
  "interview",
  "recommended2",
  "forcepush",
];

// --- Sub-Component: Info Item (Label + Value + Icon) ---
function InfoItem({ label, value, icon: Icon, fullWidth = false }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4} lg={4}>
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        {Icon && (
          <Icon
            sx={{
              fontSize: 22,
              color: "#64748B",
              mt: 0.3,
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            display="block"
            sx={{
              color: "#64748B",
              fontWeight: 600,
              fontSize: "0.75rem",
              letterSpacing: "0.3px",
              mb: 0.6,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#1E293B",
              fontWeight: 500,
              fontSize: "0.95rem",
              wordBreak: "break-word",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {value || "NA"}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
}

function PersonalDetails({
  memberInfo,
  heading,
  appliedDate,
  memberDetails,
  onOpenARCModal,
  onOpenAACModal,
  isDownloading,
  downloadPdf,
}) {
  const { type } = useParams();
  const readOnly = READ_MODE_ROUTES.includes(type);

  // --- Destructure Data ---
  const {
    memberId,
    fullName,
    addressLine1,
    mobileNumber,
    pincode,
    emailId,
    // areaName,
    // clusterName,
    // regionName,
    // districtName,
    // zoneName,
    // chapterName,
    // blockName,
    dateOfBirth,
    designation,
    occupation,
    educationalQualification,
    divisionName,
    subDivisionName,
    companyName,
  } = memberInfo;

  const image = memberDetails?.profilePhotoUrl;

  // --- Date & Age Logic ---
  const birthDate = memberInfo?.dateOfBirth
    ? new Date(memberInfo.dateOfBirth)
    : null;
  const presentDate = new Date();
  const memberAge = birthDate
    ? presentDate.getFullYear() -
      birthDate.getFullYear() -
      (presentDate.getMonth() < birthDate.getMonth() ||
      (presentDate.getMonth() === birthDate.getMonth() &&
        presentDate.getDate() < birthDate.getDate())
        ? 1
        : 0)
    : "NA";

  const date = moment();
  const currentDate = date.format("DD/MM/YYYY");
  const displayAppliedDate = !readOnly
    ? currentDate
    : moment(appliedDate).format("DD/MM/YYYY");

  // --- Org Structure Source Fallback ---
  const sourceData = memberInfo?.inProgressAppointment
    ? memberInfo
    : memberDetails || {};
  const inProgressAppointment = sourceData?.inProgressAppointment;

  const oldAreaId = memberInfo?.areaId ?? memberDetails?.areaId;
  const oldClusterId = memberInfo?.clusterId ?? memberDetails?.clusterId;
  const oldZoneId = memberInfo?.zoneId ?? memberDetails?.zoneId;
  const oldRegionId = memberInfo?.regionId ?? memberDetails?.regionId;
  const oldChapterId = memberInfo?.chapterId ?? memberDetails?.chapterId;
  const oldDistrictId = memberInfo?.districtId ?? memberDetails?.districtId;
  const oldBlockId = memberInfo?.blockId ?? memberDetails?.blockId;

  const oldAreaName = memberInfo?.areaName ?? memberDetails?.areaName;
  const oldClusterName = memberInfo?.clusterName ?? memberDetails?.clusterName;
  const oldZoneName = memberInfo?.zoneName ?? memberDetails?.zoneName;
  const oldRegionName = memberInfo?.regionName ?? memberDetails?.regionName;
  const oldChapterName = memberInfo?.chapterName ?? memberDetails?.chapterName;
  const oldDistrictName =
    memberInfo?.districtName ?? memberDetails?.districtName;
  const oldBlockName = memberInfo?.blockName ?? memberDetails?.blockName;

  // --- Org Structure String ---
  const oldOrgStructure = [
    oldAreaName,
    oldClusterName,
    oldZoneName,
    oldRegionName,
    oldChapterName,
    oldDistrictName,
    oldBlockName,
  ]
    .filter(Boolean)
    .join(" / ");

  const newOrgStructure = [
    inProgressAppointment?.currentAreaName,
    inProgressAppointment?.currentClusterName,
    inProgressAppointment?.currentZoneName,
    inProgressAppointment?.currentRegionName,
    inProgressAppointment?.currentChapterName,
    inProgressAppointment?.currentDistrictName,
    inProgressAppointment?.currentBlockName,
  ]
    .filter(Boolean)
    .join(" / ");

  const hasOrgMismatch =
    !!inProgressAppointment &&
    [
      [oldAreaId, inProgressAppointment?.currentAreaId],
      [oldClusterId, inProgressAppointment?.currentClusterId],
      [oldZoneId, inProgressAppointment?.currentZoneId],
      [oldRegionId, inProgressAppointment?.currentRegionId],
      [oldChapterId, inProgressAppointment?.currentChapterId],
      [oldDistrictId, inProgressAppointment?.currentDistrictId],
      [oldBlockId, inProgressAppointment?.currentBlockId],
    ].some(([oldId, newId]) => String(oldId ?? "") !== String(newId ?? ""));

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid #E2E8F0",
        mb: 3,
        backgroundColor: "#fff",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* --- HEADER SECTION --- */}
      <Box
        sx={{
          backgroundImage: `url(${gohonzonBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          p: { xs: 2, sm: 2.5, md: 3 },
          color: "#fff",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 100%)",
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontFamily: "'Inter', sans-serif",
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {heading}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "10px",
              padding: { xs: "10px 14px", sm: "10px 18px" },
              backdropFilter: "blur(4px)",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  opacity: 0.9,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif",
                  whiteSpace: "nowrap",
                  mb: 0.3,
                }}
              >
                {!readOnly ? "Date of applying" : "Created at"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayAppliedDate}
                </Typography>
              </Stack>
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: "rgba(255,255,255,0.4)",
                height: "45px",
                display: { xs: "none", sm: "block" },
              }}
            />

            <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Button
                onClick={downloadPdf}
                label="Download Leader Card"
                disabled={isDownloading}
                style={{
                  backgroundColor: "#fff",
                  color: "#196AAB",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  padding: "10px 24px",
                  textTransform: "none",
                  fontFamily: "'Inter', sans-serif",
                  whiteSpace: "nowrap",
                  width: "100%",
                  minWidth: "200px",
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* --- TRACKING ACTION BAR (Conditional) --- */}
      {type === "tracking" && (
        <Box
          sx={{
            backgroundColor: "#F1F5F9",
            borderBottom: "1px solid #E2E8F0",
            p: 1.5,
            px: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary"
              sx={{ fontFamily: "'Inter', sans-serif" }}
            >
              Appointment Status:
            </Typography>
            <Button
              label="ARC"
              onClick={onOpenARCModal}
              variant="outlined"
              style={{
                backgroundColor: "#fff",
                borderColor: "#196AAB",
                color: "#196AAB",
                height: "32px",
                fontSize: "13px",
                fontFamily: "'Inter', sans-serif",
              }}
            />
            <Button
              label="AAC"
              onClick={onOpenAACModal}
              variant="outlined"
              style={{
                backgroundColor: "#fff",
                borderColor: "#196AAB",
                color: "#196AAB",
                height: "32px",
                fontSize: "13px",
                fontFamily: "'Inter', sans-serif",
              }}
            />
          </Stack>
        </Box>
      )}

      {/* --- MAIN CONTENT --- */}
      <CardContent sx={{ p: 0 }}>
        <Grid container>
          {/* --- LEFT COLUMN: Profile & Structure --- */}
          <Grid
            item
            xs={12}
            md={3.5}
            sx={{
              borderRight: { md: "1px solid #E2E8F0" },
              backgroundColor: "#F8FAFC",
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Avatar - Centered */}
            <Avatar
              src={image}
              alt={fullName}
              sx={{
                width: 110,
                height: 110,
                fontSize: "2.5rem",
                bgcolor: "#196AAB",
                border: "4px solid #fff",
                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                mb: 2,
              }}
            >
              {!image && getInitials(fullName)}
            </Avatar>

            {/* Name & ID - Centered */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1E293B",
                lineHeight: 1.2,
                fontFamily: "'Inter', sans-serif",
                wordBreak: "break-word",
              }}
            >
              {fullName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                backgroundColor: "#E2E8F0",
                display: "inline-block",
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                fontWeight: 700,
                color: "#475569",
                fontSize: "0.90rem",
                fontFamily: "'Inter', sans-serif",
                mt: 1,
                mb: 2,
              }}
            >
              Member ID: {memberId}
            </Typography>

            {/* --- ORGANIZATIONAL STRUCTURE --- */}
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                p: 5,
                mb: 2.5,
                textAlign: "left",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <AccountTreeIcon sx={{ fontSize: 20, color: "#64748B" }} />
                <Typography
                  variant="caption"
                  display="block"
                  color="#64748B"
                  fontWeight={600}
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Organisational Structure:
                </Typography>
              </Stack>

              {hasOrgMismatch ? (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      border: "1px solid #FECACA",
                      backgroundColor: "#FEF2F2",
                    }}
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        color: "#DC2626",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        letterSpacing: "0.3px",
                        mb: 0.6,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Transferred To:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#B91C1C",
                        fontWeight: 600,
                        lineHeight: 1.5,
                        fontSize: "0.9rem",
                        wordBreak: "break-word",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {oldOrgStructure || "NA"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        color: "#196AAB",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        letterSpacing: "0.3px",
                        mb: 0.6,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Application Raised At:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#0F172A",
                        fontWeight: 600,
                        lineHeight: 1.5,
                        fontSize: "0.9rem",
                        wordBreak: "break-word",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {newOrgStructure || "NA"}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#0F172A",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {oldOrgStructure || "NA"}
                </Typography>
              )}
            </Box>

            {/* Division Info */}
            <Paper
              elevation={0}
              sx={{ width: "100%", bgcolor: "transparent", p: 0 }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px dashed #CBD5E1",
                    pb: 1.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ApartmentIcon sx={{ fontSize: 18, color: "#64748B" }} />
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#64748B"
                      sx={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Division
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="#334155"
                    sx={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {divisionName || "NA"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SubdirectoryArrowRightIcon
                      sx={{ fontSize: 18, color: "#64748B" }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#64748B"
                      sx={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Sub Division
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="#334155"
                    sx={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {getSubDivisionDisplayName(subDivisionName) || "NA"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* --- RIGHT COLUMN: Details Grid --- */}
          <Grid item xs={12} md={8.5} sx={{ p: 10.5 }}>
            {/* Personal Details Header */}
            <Typography
              variant="h6"
              sx={{
                color: "#196AAB",
                fontWeight: 700,
                fontSize: "1.1rem",
                mb: 3.5,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Personal Details
            </Typography>

            <Grid container spacing={3}>
              <InfoItem
                label="Date Of Birth"
                value={
                  dateOfBirth ? moment(dateOfBirth).format("DD/MM/YYYY") : "NA"
                }
                icon={CakeIcon}
              />
              <InfoItem label="Age" value={memberAge} icon={PersonIcon} />
              <InfoItem
                label="Contact Number"
                value={mobileNumber}
                icon={PhoneIcon}
              />

              <InfoItem
                label="Address"
                value={addressLine1}
                icon={HomeIcon}
                fullWidth
              />

              <InfoItem label="Pincode" value={pincode} icon={PinDropIcon} />
              <InfoItem label="Email" value={emailId} icon={EmailIcon} />

              {/* Divider Line */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
              </Grid>

              <InfoItem
                label="Name Of Company"
                value={companyName}
                icon={BusinessIcon}
              />
              <InfoItem label="Occupation" value={occupation} icon={WorkIcon} />
              <InfoItem
                label="Designation"
                value={designation}
                icon={BadgeIcon}
              />
              <InfoItem
                label="Educational Qualification"
                value={educationalQualification}
                icon={SchoolIcon}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default PersonalDetails;
